import dayjs from "dayjs";
import i18n from "multilingual";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export const formatDate = (date, separator = "-", format = "MM/DD/YYYY") => {
  if (!date) {
    return "Not Available";
  }
  if (!dayjs(date).isValid()) {
    return "Invalid date";
  }
  return dayjs(date)?.format(format.replaceAll("-", separator));
};

// unixDate function with error handling
export const unixDate = (date) => {
  if (!dayjs(date).isValid()) {
    return "Invalid date";
  }
  return dayjs(date).unix();
};

// epochConverter function with error handling
export const epochConverter = (data) => {
  // Assuming data should be a number representing Unix timestamp
  if (typeof data !== "number" || isNaN(data)) {
    return "Invalid data";
  }
  return dayjs.unix(data).format("MM/DD/YYYY hh:mm A");
};

export const getRelativeTime = (timestamp) => {
  if (!timestamp) return "";
  return dayjs(timestamp).fromNow();
};

export const getUnixMs = (date = new Date()) => {
  return dayjs(date).valueOf();
};

// isDateEqual function with error handling
export const isDateEqual = (date1, date2) => {
  if (!dayjs(date1).isValid() || !dayjs(date2).isValid()) {
    return "One or both dates are invalid";
  }
  const isEqual =
    dayjs(date1)?.format("MM/DD/YYYY") === dayjs(date2)?.format("MM/DD/YYYY");

  return isEqual;
};

export const formatDateWithOrdinalSuffix = (dateString) => {
  const date = dayjs(dateString);

  function getOrdinalSuffix(day) {
    if (day >= 11 && day <= 13) {
      return "th";
    }
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }

  const formattedDate = `${date.format("MMM")} ${date.format(
    "D"
  )}${getOrdinalSuffix(date.date())}, ${date.format("YYYY")}`;
  return formattedDate;
};

export function getDurationString(startTimeInput) {
  const startTime = dayjs();
  const endTime = dayjs(startTimeInput);

  const isPast = endTime.isBefore(startTime);

  const durationInMilliseconds = Math.abs(endTime.diff(startTime));

  const days = Math.floor(durationInMilliseconds / (24 * 60 * 60 * 1000));
  const hours = Math.floor(
    (durationInMilliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
  );
  const minutes = Math.floor(
    (durationInMilliseconds % (60 * 60 * 1000)) / (60 * 1000)
  );

  let result = "";

  if (days > 0) {
    result += `${days} ${i18n?.t(`common.${days !== 1 ? "DAYS" : "DAY"}`)} `;
  }

  if (hours > 0) {
    result += `${hours} ${i18n?.t(
      `common.${hours !== 1 ? "HOURS" : "HOUR"}`
    )} `;
  }

  if (minutes > 0) {
    result += `${minutes} ${i18n?.t(
      `common.${minutes !== 1 ? "MINUTES" : "MINUTE"}`
    )} `;
  }

  result = result.trim(); // Remove any trailing whitespace

  if (isPast) {
    return result + " " + i18n?.t("common.AGO");
  } else {
    return result + " " + i18n?.t("common.LEFT");
  }
}

export const dayDifference = (date) => {
  const currentDate = dayjs();
  const endDate = dayjs(date);
  let daysDifference;

  if (isDateEqual(endDate, currentDate)) {
    daysDifference = 0;
  } else if (endDate.diff(currentDate, "day") >= 0) {
    daysDifference = endDate.diff(currentDate, "day") + 1;
  } else {
    daysDifference = endDate.diff(currentDate, "day");
  }

  let timeLeft =
    daysDifference >= 0
      ? daysDifference <= 1
        ? `${daysDifference} ${i18n.t("appointments.DAY_LEFT")}`
        : `${daysDifference} ${i18n.t("dashboard.DAYS_LEFT")}`
      : `${i18n.t("appointments.OVERDUE_BY")} ${daysDifference * -1} ${
          daysDifference === -1 ? i18n.t("common.DAY") : i18n.t("common.DAYS")
        }`;

  if (daysDifference === 0) {
    timeLeft = `${i18n.t("appointments.DUE_TODAY")}`;
  }

  return {
    value: daysDifference,
    string: timeLeft,
  };
};
